/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
    Select,
    SelectOption,
    FlexCol,
} from 'sarsaparilla';
import { distance } from '@turf/distance';
import { point } from 'shared-ui/maps/turfUtils';
import * as tripPlannerActions from '../actions/tripPlanner';
import { sortByDistance } from '../utils/tripUtils';

const propTypes = {
    showTripItemAddModal: PropTypes.bool,
    trip: PropTypes.object,
    activeDestinationIndex: PropTypes.number,
    tripItemToAdd: PropTypes.object,
    dispatch: PropTypes.func,
};

const defaultProps = {
    showTripItemAddModal: false,
    tripItemToAdd: {},
};

export class AddTripItemModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isModalOpen: false,
            selectedDestination: 'closest', // 'closest', 'new', or destination index
        };

        this.onAdd = this.onAdd.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.onModalOpen = this.onModalOpen.bind(this);
        this.onDestinationChange = this.onDestinationChange.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.showTripItemAddModal !== this.props.showTripItemAddModal) {
            this.setState({
                isModalOpen: nextProps.showTripItemAddModal,
                selectedDestination: nextProps.showTripItemAddModal
                    ? 'closest'
                    : this.state.selectedDestination,
            });
        }
    }

    onDestinationChange(event) {
        this.setState({ selectedDestination: event.currentTarget.value });
    }

    findClosestDestination(tripItemToAdd, trip, activeDestinationIndex) {
        // Find the closest destination by distance to the item being added
        // Exclude index 0 (starting point) as users don't visit attractions there
        let closestDestinationIndex = activeDestinationIndex;
        let closestDestinationName = 'Closest Destination';

        if (
            tripItemToAdd.lat &&
            tripItemToAdd.lng &&
            trip.destinations &&
            trip.destinations.length > 1
        ) {
            const itemPoint = point([tripItemToAdd.lng, tripItemToAdd.lat]);
            let minDistance = Infinity;

            trip.destinations.forEach((destination, index) => {
                // Skip the starting point (index 0)
                if (index === 0) return;

                if (destination.lat && destination.lng) {
                    const destPoint = point([destination.lng, destination.lat]);
                    const dist = distance(itemPoint, destPoint, { units: 'miles' });

                    if (dist < minDistance) {
                        minDistance = dist;
                        closestDestinationIndex = index;
                        closestDestinationName = `${destination.name} (Closest destination)`;
                    }
                }
            });
        } else if (
            activeDestinationIndex !== null &&
            activeDestinationIndex > 0 &&
            trip.destinations[activeDestinationIndex]
        ) {
            // Fallback to active destination if no coordinates available, but not if it's the starting point
            closestDestinationName = `${trip.destinations[activeDestinationIndex].name} (Current destination)`;
        } else if (trip.destinations && trip.destinations.length > 1) {
            // If active destination is starting point, default to first real destination
            closestDestinationIndex = 1;
            closestDestinationName = `${trip.destinations[1].name} (First destination)`;
        }

        return { closestDestinationIndex, closestDestinationName };
    }

    onAdd() {
        const { selectedDestination } = this.state;
        const { dispatch, trip, activeDestinationIndex, tripItemToAdd } = this.props;

        if (selectedDestination === 'new') {
            // Create a new destination
            const newDestination = {
                ...tripItemToAdd,
                name: tripItemToAdd.name,
                duration: 1,
                items: [tripItemToAdd],
            };

            dispatch(tripPlannerActions.addDestination(newDestination));

            const updatedTrip = {
                ...trip,
                destinations: sortByDistance([
                    ...(trip.destinations ?? []),
                    newDestination,
                ]),
            };

            setTimeout(() => {
                dispatch(
                    tripPlannerActions.setActiveDestinationIndex(trip.destinations.length)
                );
                dispatch(tripPlannerActions.setTrip(updatedTrip, true));
                dispatch(tripPlannerActions.fetchDrivingDirections(updatedTrip));
                dispatch(tripPlannerActions.fetchDetailedRoute(updatedTrip));
            }, 100);
        } else {
            // Add to existing destination
            let destinationIndex;

            if (selectedDestination === 'closest') {
                // Find the closest destination by distance
                if (
                    tripItemToAdd.lat &&
                    tripItemToAdd.lng &&
                    trip.destinations &&
                    trip.destinations.length > 1
                ) {
                    const { closestDestinationIndex } = this.findClosestDestination(
                        tripItemToAdd,
                        trip,
                        activeDestinationIndex
                    );
                    destinationIndex = closestDestinationIndex;
                } else {
                    destinationIndex =
                        activeDestinationIndex !== null && activeDestinationIndex > 0
                            ? activeDestinationIndex
                            : 1;
                }
            } else {
                destinationIndex = parseInt(selectedDestination, 10);
            }

            // Ensure destination exists
            if (trip.destinations.length <= destinationIndex) {
                dispatch(
                    tripPlannerActions.addDestination({
                        ...tripItemToAdd,
                        items: [],
                    })
                );
            }

            dispatch(tripPlannerActions.addItem(destinationIndex, tripItemToAdd));
            dispatch(
                tripPlannerActions.setActiveDestinationIndex(
                    destinationIndex,
                    false,
                    true,
                    true
                )
            );
        }

        this.onModalClose();
    }

    onModalOpen() {}

    onModalClose() {
        this.props.dispatch(tripPlannerActions.setShowTripItemAddSModal(false));
        this.setState({ isModalOpen: false });
    }

    render() {
        const { trip, activeDestinationIndex, tripItemToAdd } = this.props;
        const { selectedDestination } = this.state;

        // Find the closest destination by distance to the item being added
        // Exclude index 0 (starting point) as users don't visit attractions there
        const { closestDestinationIndex, closestDestinationName } =
            this.findClosestDestination(tripItemToAdd, trip, activeDestinationIndex);

        return (
            <StyledModal
                isOpen={this.state.isModalOpen}
                onAfterOpen={this.onModalOpen}
                onRequestClose={this.onModalClose}
                role="complementary"
                ariaHideApp={false}
                contentLabel="Add Item to Trip"
            >
                <div className="trip-planner-add-item-modal">
                    <div className="rec-modal-content-wrap">
                        <h3>Add to Itinerary</h3>
                        <FlexCol>
                            <p>
                                Add <strong>{tripItemToAdd.name}</strong> to your trip:
                            </p>

                            <Select
                                id="destination-select"
                                label="Choose destination"
                                value={selectedDestination}
                                onChange={this.onDestinationChange}
                            >
                                <SelectOption value="closest">
                                    {closestDestinationName}
                                </SelectOption>
                                {trip.destinations &&
                                    trip.destinations.map((destination, index) => {
                                        // Skip the starting point (index 0) and closest destination
                                        if (
                                            index === 0 ||
                                            index === closestDestinationIndex
                                        ) {
                                            return null;
                                        }

                                        return (
                                            <SelectOption
                                                key={index}
                                                value={index.toString()}
                                            >
                                                {destination.name}
                                            </SelectOption>
                                        );
                                    })}
                                <SelectOption value="new">
                                    Create New Destination (+ 1 day)
                                </SelectOption>
                            </Select>
                        </FlexCol>
                    </div>
                </div>
                <ModalActions>
                    <ButtonGroup>
                        <Button title="Add" aria-label="Add" onClick={this.onAdd}>
                            Add to Itinerary
                        </Button>
                        <Button
                            appearance="tertiary"
                            title="Cancel"
                            aria-label="Cancel"
                            onClick={this.onModalClose}
                        >
                            Cancel
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
        );
    }
}

AddTripItemModal.propTypes = propTypes;
AddTripItemModal.defaultProps = defaultProps;

function select(state) {
    return {
        trip: state.tripPlanner.trip,
        showTripItemAddModal: state.tripPlanner.showTripItemAddModal,
        tripItemToAdd: state.tripPlanner.tripItemToAdd,
        activeDestinationIndex: state.tripPlanner.activeDestinationIndex,
    };
}

export default connect(select)(AddTripItemModal);
